/**
 * The rental agreement, as it was signed.
 *
 * `booking.rentalAgreement` is a frozen snapshot, not a PDF: the parties with
 * their ID numbers, the bike with its plate, the period, the money, and the
 * clause text itself copied in at generation time. It is frozen precisely so
 * that editing app/constants/rentalTerms.ts never rewrites what a past rental
 * is shown to have agreed to — so this renders the snapshot's OWN clauses and
 * only falls back to the current mirror for agreements generated before the
 * clauses were frozen onto them.
 *
 * The panel knew this object existed and said one word about it: "Generated".
 * The clauses say the agreement "may be presented to relevant authorities as
 * a reference document", which is impossible if nobody can read it.
 *
 * Signatures are stored as vector paths with the canvas size they were drawn
 * at (app/components/SignaturePad.tsx), so they scale cleanly here.
 */
import { useEffect } from 'react';
import { T, tone } from './theme';
import { Icon } from './ui';
import { AGREEMENT_VERSION, TERMS_CLAUSES } from './rentalTerms';

const fmtStamp = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/** A stored signature, drawn at whatever width there is room for. */
function Signature({ sig, width = 200 }) {
  if (!sig?.paths?.length || !sig.width) return null;
  const height = (sig.height / sig.width) * width;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${sig.width} ${sig.height}`}
      style={{ display: 'block', maxWidth: '100%' }}
      aria-label="Signature"
    >
      {sig.paths.map((d, i) => (
        <path
          key={i}
          d={d}
          stroke={T.textPrimary}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      ))}
    </svg>
  );
}

function Field({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0', alignItems: 'baseline' }}>
      <div style={{ color: T.textMuted, fontSize: 12, flex: '0 0 92px' }}>{label}</div>
      <div style={{
        color: value ? T.textPrimary : T.textMuted, fontSize: 13, fontWeight: value ? 600 : 400,
        minWidth: 0, wordBreak: 'break-word',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
      }}>{value || 'Not recorded'}</div>
    </div>
  );
}

function Party({ title, name, phone, nid, sig, signedAt, isMobile }) {
  const t = tone(sig ? 'success' : 'warning');
  return (
    <div style={{
      flex: 1, minWidth: 0,
      backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
      borderRadius: 10, padding: 12,
    }}>
      <div style={{
        color: T.textMuted, fontSize: 10.5, fontWeight: 800,
        letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6,
      }}>{title}</div>
      <Field label="Name" value={name} />
      <Field label="Phone" value={phone ? `+960 ${phone}` : ''} />
      <Field label="ID number" value={nid} mono />
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.cardBorder}` }}>
        {sig ? (
          <>
            <Signature sig={sig} width={isMobile ? 150 : 190} />
            <div style={{ color: t.color, fontSize: 11.5, marginTop: 4 }}>
              Signed {fmtStamp(sig.signedAt || signedAt) || 'at an unrecorded time'}
            </div>
          </>
        ) : (
          <div style={{ color: t.color, fontSize: 12, fontWeight: 600 }}>Not signed</div>
        )}
      </div>
    </div>
  );
}

export function AgreementModal({ booking, isMobile, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const a = booking?.rentalAgreement;
  const sigs = booking?.agreementSignatures || {};
  // The snapshot's own clauses are what was agreed. The mirror is only a
  // fallback for agreements written before the text was frozen onto them,
  // and it is labelled as such rather than passed off as the real thing.
  const frozen = Array.isArray(a?.clauses) && a.clauses.length > 0;
  const clauses = frozen ? a.clauses : TERMS_CLAUSES;
  const version = a?.agreementVersion ?? null;

  // Both parties sign against a hash of the snapshot. Two different hashes
  // means they did not sign the same document, which is the one thing this
  // screen exists to be able to show.
  const hashes = [sigs.renter?.agreementHash, sigs.owner?.agreementHash].filter(Boolean);
  const hashMismatch = hashes.length === 2 && hashes[0] !== hashes[1];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'var(--backdrop)',
        zIndex: 500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: isMobile ? 0 : 20, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: T.bg, border: `1px solid ${T.cardBorder}`,
          borderRadius: isMobile ? 0 : 16, width: '100%', maxWidth: 760,
          minHeight: isMobile ? '100%' : 0,
          margin: isMobile ? 0 : 'auto 0', boxShadow: T.shadowLg,
        }}
      >
        {/* Stays put: the clauses are long and Close should not require
            scrolling back to the top of them. */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: isMobile ? '14px 16px calc(14px)' : '16px 20px',
          paddingTop: isMobile ? 'calc(14px + env(safe-area-inset-top))' : 16,
          backgroundColor: T.bg, borderBottom: `1px solid ${T.cardBorder}`,
          borderRadius: isMobile ? 0 : '16px 16px 0 0',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: T.textPrimary, fontSize: 16, fontWeight: 700 }}>Rental Agreement</div>
            <div style={{ color: T.textMuted, fontSize: 12, marginTop: 2 }}>
              {booking?.bookingRef ? `${booking.bookingRef} · ` : ''}
              {version != null ? `version ${version}` : 'version not recorded'}
              {a?.generatedAt ? ` · generated ${fmtStamp(a.generatedAt)}` : ''}
              {a?.generatedBy ? ` by ${a.generatedBy}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="ui-btn" aria-label="Close" style={{
            backgroundColor: T.cardInner, border: `1px solid ${T.cardBorder}`,
            borderRadius: 9, padding: '8px 10px', cursor: 'pointer', flexShrink: 0,
          }}>
            <Icon name="x" size={15} color={T.textSecondary} />
          </button>
        </div>

        <div style={{
          padding: isMobile ? 16 : 20,
          paddingBottom: isMobile ? 'calc(24px + env(safe-area-inset-bottom))' : 20,
          display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          {hashMismatch && (
            <div style={{
              backgroundColor: T.dangerDim, border: `1px solid ${T.dangerBorder}`,
              borderRadius: 10, padding: 12, color: T.danger, fontSize: 12.5, lineHeight: 1.6,
            }}>
              The two signatures were made against different versions of this agreement.
              Each party signs a hash of the snapshot, and these do not match — so they did
              not sign the same document. Worth understanding before relying on it.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12 }}>
            <Party
              title="Renter"
              name={a.renterName} phone={a.renterPhone} nid={a.renterNid}
              sig={sigs.renter} isMobile={isMobile}
            />
            <Party
              title="Owner"
              name={a.ownerName} phone={a.ownerPhone} nid={a.ownerNid}
              sig={sigs.owner} isMobile={isMobile}
            />
          </div>

          <div style={{
            display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12,
          }}>
            <div style={{
              flex: 1, minWidth: 0, backgroundColor: T.card,
              border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: 12,
            }}>
              <div style={{
                color: T.textMuted, fontSize: 10.5, fontWeight: 800,
                letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6,
              }}>Bike</div>
              <Field label="Model" value={a.bikeModel} />
              <Field label="Plate" value={a.plate} mono />
              <Field label="Engine" value={a.cc ? `${a.cc}cc` : ''} />
              <Field label="Year" value={a.year} />
            </div>
            <div style={{
              flex: 1, minWidth: 0, backgroundColor: T.card,
              border: `1px solid ${T.cardBorder}`, borderRadius: 10, padding: 12,
            }}>
              <div style={{
                color: T.textMuted, fontSize: 10.5, fontWeight: 800,
                letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6,
              }}>Terms</div>
              <Field label="From" value={[a.startDate, a.startTime].filter(Boolean).join(' ')} />
              <Field label="Until" value={[a.endDate, a.endTime].filter(Boolean).join(' ')} />
              <Field label="Total" value={a.total != null ? `MVR ${a.total}` : ''} />
              <Field
                label="Late fee"
                value={Number(a.lateFeePerHour) > 0
                  ? `MVR ${a.lateFeePerHour} per hour`
                  : 'None — this rental charges no late fee'}
              />
            </div>
          </div>

          <div>
            <div style={{
              color: T.textMuted, fontSize: 10.5, fontWeight: 800,
              letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8,
            }}>
              Clauses
            </div>
            {!frozen && (
              <div style={{ color: T.warning, fontSize: 12, lineHeight: 1.6, marginBottom: 10 }}>
                This agreement predates the clause text being frozen onto the snapshot, so
                what follows is today&rsquo;s wording (version {AGREEMENT_VERSION}) and not
                necessarily what these two agreed to.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {clauses.map((c, i) => (
                <div key={i} style={{
                  backgroundColor: T.card, border: `1px solid ${T.cardBorder}`,
                  borderRadius: 10, padding: 12,
                }}>
                  <div style={{ color: T.textPrimary, fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
                    {i + 1}. {c.title}
                  </div>
                  <div style={{ color: T.textSecondary, fontSize: 12.5, lineHeight: 1.7 }}>{c.text}</div>
                </div>
              ))}
            </div>
          </div>

          {(sigs.renter?.agreementHash || sigs.owner?.agreementHash) && (
            <div style={{ color: T.textMuted, fontSize: 11, lineHeight: 1.7, wordBreak: 'break-all' }}>
              {/* Shown so an altered snapshot is detectable: re-hashing the
                  agreement and comparing is the whole point of storing it. */}
              {sigs.renter?.agreementHash && <div>Renter signed hash {sigs.renter.agreementHash}</div>}
              {sigs.owner?.agreementHash && <div>Owner signed hash {sigs.owner.agreementHash}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
